import {faCalendar} from '@fortawesome/pro-regular-svg-icons';
import {faNewspaper} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button} from 'antd';
import {ReactElement, useContext, useMemo, useState} from 'react';

import {appRoute} from '../../../app-route';
import {PageHeaderWithLicenseTag} from '../../../component/header/header-with-license-tag/page-header-with-license-tag';
import {LicensePageCard} from '../../../component/license-page-card/license-page-card';
import {Meta} from '../../../component/meta/meta';
import {UsetifulNameProductEnum} from '../../../component/usetiful/usetiful-const';
import {BreadCrumbs} from '../../../layout/bread-crumbs/bread-crumbs';
import {NavigationLink} from '../../../layout/navigation-link/navigation-link';
import {Page} from '../../../layout/page/page';
import {MainPageContainer} from '../../../layout/page-card/main-page-card';
import {PageCard} from '../../../layout/page-card/page-card';
import {useLicenses} from '../../../provider/license/license-hook';
import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {MainFilterContext} from '../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../provider/main-filter/main-filter-type';
import {useUser} from '../../../provider/user/user-hook';
import {HelpLinkFeatureFlagEnum} from '../../../service/feature-flags/feature-flag-const';
import {useFeatureFlag} from '../../../service/feature-flags/use-feature-flag';
import {usePosts, usePostsCatalogs} from '../../../service/posts/posts-hooks';
import {emptyPublicationListFilter} from '../../../service/publication/publication-filter-type';
import {FeaturesEnum} from '../../../service/user/user-type';
import {getEnumValueEnsure} from '../../../util/enum';
import {useUrl} from '../../../util/url-hook/url-hook';

import {PostsTabEnum} from './posts-const';
import {PostsCreateButton} from './posts-create-button/posts-create-button';
import {
    PostsFilterQueriesType,
    PostsFilterStateUrlQueryNameEnum,
    PostsUrlQueriesType,
} from './posts-filter/posts-filter-type';
import {PostsHeaderControl} from './posts-header-control/posts-header-control';
import {getPostsStateFromUrlQuery} from './posts-state/posts-state-helper';
import {PostsTableCard} from './posts-table-card/posts-table-card';
import * as postsCalendarStyles from './calendar/posts-calendar.scss';
import * as styles from './posts.scss';

export function Posts(): ReactElement {
    const {getLocalizedString} = useLocale();

    const {postsManagement, postsManagementCalendar, accountLinkingSettings} = appRoute;

    const {queries, setQuery} = useUrl<PostsUrlQueriesType>();

    // Stories/promotions/events tabs are gated behind a feature flag (per-user rollout); when it is
    // off only the Posts tab exists, so any other tab in the URL is forced back to Posts.
    const isPublicationCatalogEnabled = useFeatureFlag(HelpLinkFeatureFlagEnum.publicationCatalog);

    const activeTab = isPublicationCatalogEnabled
        ? getEnumValueEnsure(PostsTabEnum, queries[PostsFilterStateUrlQueryNameEnum.tab], PostsTabEnum.Posts)
        : PostsTabEnum.Posts;

    const [publicationFilter, setPublicationFilter] = useState(emptyPublicationListFilter);

    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    const postsFilterQueries: PostsFilterQueriesType = useMemo(
        () => getPostsStateFromUrlQuery(queries),
        // Depend only on posts-relevant query params (not `tab`), so switching tabs doesn't refetch /v1/posts/.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            queries[PostsFilterStateUrlQueryNameEnum.statuses],
            queries[PostsFilterStateUrlQueryNameEnum.createdAtBefore],
            queries[PostsFilterStateUrlQueryNameEnum.createdAtAfter],
            queries[PostsFilterStateUrlQueryNameEnum.catalogs],
        ]
    );

    const {user} = useUser();

    const {licenses} = useLicenses();
    const license = licenses[FeaturesEnum.posts];

    const isLicenseExpired = !license?.isActive;

    const postsMainFilterKey = user?.isDemoCpUser ? '' : mainFilterKey;

    const {
        result: posts,
        isInProgress: isPostsInProgress,
        processError: postsProcessError,
        refresh,
        cursorPagination,
        count: postsCount,
    } = usePosts({
        filter: postsFilterQueries,
        mainFilterKey: postsMainFilterKey,
        // Only fetch the posts list while the Posts tab is active to avoid /v1/posts requests on other tabs.
        enabled: activeTab === PostsTabEnum.Posts,
    });

    const {
        result: catalogs,
        isInProgress: isCatalogsInProgress,
        processError: catalogsProcessError,
    } = usePostsCatalogs();

    const processError = postsProcessError || catalogsProcessError;
    const isInProgress = isPostsInProgress || isCatalogsInProgress;

    return (
        <Page
            isTopFilterHidden={user?.isDemoCpUser}
            renderContainer={(pageContent) => <MainPageContainer>{pageContent}</MainPageContainer>}
        >
            <PageCard>
                <Meta title={getLocalizedString('CATEGORY_NAME__POSTS')} />
                <BreadCrumbs list={[{path: postsManagement.path, titleLangKey: 'CATEGORY_NAME__POSTS'}]} />

                <PageHeaderWithLicenseTag
                    headerLangKey="CATEGORY_NAME__POSTS"
                    licenseActive={license?.isActive}
                    productName={UsetifulNameProductEnum.POSTS}
                    trialLicense={license?.isTrial}
                >
                    <div className={styles.posts__header__button_wrapper}>
                        <NavigationLink
                            className={postsCalendarStyles.PostCalendar_link}
                            to={postsManagementCalendar.path}
                        >
                            <Button
                                className={postsCalendarStyles.PostCalendar_button}
                                disabled={isLicenseExpired}
                                icon={
                                    <FontAwesomeIcon
                                        className={postsCalendarStyles.PostCalendar_icon}
                                        icon={faCalendar}
                                    />
                                }
                                type="default"
                            >
                                <Locale stringKey="POSTS__CALENDAR_PAGE" />
                            </Button>
                        </NavigationLink>

                        <NavigationLink to={accountLinkingSettings.path}>
                            <Button disabled={isLicenseExpired}>
                                <Locale stringKey="POSTS__ACCOUNTS_SETTINGS" />
                            </Button>
                        </NavigationLink>

                        <PostsCreateButton activeTab={activeTab} isLicenseExpired={isLicenseExpired} />
                    </div>
                </PageHeaderWithLicenseTag>
                <PostsHeaderControl
                    activeTab={activeTab}
                    catalogs={catalogs || []}
                    filter={publicationFilter}
                    isLicenseExpired={isLicenseExpired}
                    onFilterChange={setPublicationFilter}
                />
            </PageCard>

            <LicensePageCard
                icon={<FontAwesomeIcon icon={faNewspaper} />}
                license={license}
                productLandKey="CATEGORY_NAME__POSTS"
            />

            <PostsTableCard
                activeTab={activeTab}
                areCatalogTabsVisible={isPublicationCatalogEnabled}
                cursorPagination={cursorPagination}
                isInProgress={isInProgress}
                isLicenseExpired={isLicenseExpired}
                onTabChange={(key) => {
                    setPublicationFilter(emptyPublicationListFilter);
                    setQuery({[PostsFilterStateUrlQueryNameEnum.tab]: key});
                }}
                posts={posts || []}
                postsCount={postsCount}
                processError={Boolean(processError)}
                publicationFilter={publicationFilter}
                refresh={refresh}
            />
        </Page>
    );
}
